import { Flex, Typography, tokens, View } from "@sapphire-react/core";
import { Fragment } from "react/jsx-runtime";
import { EMPTY_VALUE } from "../../constants";
import { premiumValues } from "../../dataValues";
import type { LoanInsuranceForm } from "../../types";
import { ChartLegend } from "./ChartLegend";
import { Donut } from "./Donut";

type Props = {
  data?: LoanInsuranceForm;
  isValid: boolean;
};

export const DonutAndNumbers = ({ data, isValid }: Props) => {
  const { age, paymentProtection, lifeProtection } = data ?? {};

  const premiumItem = premiumValues.livskydd.find(
    (item) => item.age === parseInt(age ?? ""),
  );
  const paymentProtectionValue =
    isValid && paymentProtection
      ? Math.round(parseInt(paymentProtection) * 0.04)
      : EMPTY_VALUE;
  const lifeProtectionValue =
    isValid && lifeProtection
      ? Math.round((premiumItem?.premium ?? 0) * parseInt(lifeProtection))
      : EMPTY_VALUE;

  const chartItems = [
    {
      value: paymentProtection ? paymentProtectionValue : 0,
      color: tokens.color.backgroundDecorative1.value,
      legendTitle: 'Månadskostnad betalskydd',
      borderRadius: 0,
      testId: 'premium-payment-protection',
      displayLabel: isValid && paymentProtection ? null : `${EMPTY_VALUE} kr`,
    },
    {
      value: lifeProtection ? lifeProtectionValue : 0,
      color: tokens.color.backgroundDecorative3.value,
      legendTitle: 'Månadskostnad livskydd',
      borderRadius: 0,
      testId: 'premium-life-insurance',
      displayLabel: isValid && lifeProtection ? null : `${EMPTY_VALUE} kr`,
    },
  ];

  const total = isValid
    ? chartItems.reduce((prev, curr) => prev + (Number(curr.value) ?? 0), 0)
    : EMPTY_VALUE;

  const segments = total
    ? chartItems.map((item) => ({
        percentage: isValid
          ? ((Number(item.value) ?? 0) * 100) / Number(total)
          : 50,
        color: item.color,
      }))
    : [];
  const donutSize = 224;

  return (
    <View
      alignItems={"center"}
      borderRadius={tokens.size.radiusSm.value}
      backgroundColor={tokens.color.backgroundSurface.value}
    >
      <Flex justifyContent={"center"} alignItems={"center"}>
        <Donut
          segments={segments}
          donutHeight={donutSize}
          donutWidth={donutSize}
          circleSizeX={donutSize}
          circleSizeY={donutSize}
          strokeWidth={2.6}
        >
          <>
            <Typography.Heading data-test-id={"donut-total"} level={4}>
              {`${total} kr`}
            </Typography.Heading>
            <Typography.Body
              size={"sm"}
              isBold
              UNSAFE_style={{
                color: tokens.color.foregroundSecondary.value,
                fontWeight: 400,
              }}
            >
              /mån
            </Typography.Body>
          </>
        </Donut>
      </Flex>

      <Flex
        flexDirection={"column"}
        width={"100%"}
        UNSAFE_style={{ boxSizing: "border-box" }}
        padding-bottom={tokens.size.spacingLg}
        paddingLeft={{
          base: tokens.size.spacingMd,
          sm: tokens.size.spacing3xl,
        }}
        paddingRight={{
          base: tokens.size.spacingMd,
          sm: tokens.size.spacing3xl,
        }}
      >
        {chartItems.map((item, index) => (
          <Fragment key={`legend-${index}`}>
            <ChartLegend chartItem={item} currency={"kr"} />
          </Fragment>
        ))}
      </Flex>
    </View>
  );
};
